import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Mic, Square, Loader2 } from "lucide-react";
import { api, apiError } from "@/lib/api";

// Optional push-to-dictate for a text field. Appends transcribed text; typing always works independently.
export default function DictateButton({ workspaceId, onText, disabled, title = "Dictate" }) {
  const [state, setState] = useState("idle"); // idle|recording|processing
  const mediaRef = useRef(null); const chunksRef = useRef([]); const streamRef = useRef(null);

  const start = async (e) => {
    e.preventDefault();
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") { toast.error("Recording is not supported in this browser — type instead."); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      let mime = ""; for (const m of ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"]) { if (MediaRecorder.isTypeSupported(m)) { mime = m; break; } }
      const mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream); mediaRef.current = mr; chunksRef.current = [];
      mr.ondataavailable = (ev) => ev.data.size && chunksRef.current.push(ev.data);
      mr.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
        if (!blob || blob.size < 800) { setState("idle"); toast.error("No audio captured."); return; }
        setState("processing");
        try {
          const fd = new FormData(); fd.append("file", blob, "dictation.webm");
          const { data } = await api.post(`/voice/transcribe?workspace_id=${workspaceId}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
          const txt = (data.transcript || "").trim();
          if (txt) onText(txt); else toast.error("No speech detected.");
        } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Transcription failed — type instead."); }
        finally { setState("idle"); }
      };
      mr.start(); setState("recording");
    } catch { toast.error("Microphone unavailable or denied — type instead."); setState("idle"); }
  };
  const stop = (e) => { e.preventDefault(); try { mediaRef.current?.stop(); } catch {} };

  return (
    <button type="button" onClick={state === "recording" ? stop : start} disabled={disabled || state === "processing"}
      title={state === "recording" ? "Stop dictation" : title} data-testid="dictate-btn"
      className="inline-flex items-center justify-center transition-colors duration-150 disabled:opacity-40"
      style={{ width: 28, height: 28, borderRadius: 2, border: "1px solid var(--si-border)",
        background: state === "recording" ? "rgba(239,68,68,0.15)" : "var(--si-surface)",
        color: state === "recording" ? "var(--si-danger)" : "var(--si-text-2)" }}>
      {state === "processing" ? <Loader2 size={14} className="animate-spin" /> : state === "recording" ? <Square size={13} /> : <Mic size={14} />}
    </button>
  );
}
